import { useQuery } from '@apollo/client';
import _ from 'lodash';
import React from 'react';
import SVGIcon from '../components/SVGIcon';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';
import favoriteIcon from '../icons/non-prefilled/16x16/favorite.svg';
import mentionIcon from '../icons/non-prefilled/16x16/mention.svg';
import openIcon from '../icons/non-prefilled/16x16/open.svg';
import pinOutlineIcon from '../icons/non-prefilled/16x16/pinOutline.svg';
import privateIcon from '../icons/non-prefilled/16x16/private.svg';
import shortcutIcon from '../icons/non-prefilled/16x16/shortcut.svg';
import textIcon from '../icons/non-prefilled/16x16/text.svg';
import verifiedIcon from '../icons/non-prefilled/16x16/verified.svg';
import aiChipSparkIcon from '../icons/non-prefilled/sidebar/aiChipSpark.svg';
import androidIcon from '../icons/non-prefilled/sidebar/android.svg';
import angleBracketsIcon from '../icons/non-prefilled/sidebar/angleBrackets.svg';
import bookCloseIcon from '../icons/non-prefilled/sidebar/bookClose.svg';
import bookmarksIcon from '../icons/non-prefilled/sidebar/bookmarks.svg';
import cloudIcon from '../icons/non-prefilled/sidebar/cloud.svg';
import cogIcon from '../icons/non-prefilled/sidebar/cog.svg';
import colorBrushIcon from '../icons/non-prefilled/sidebar/colorBrush.svg';
import currencyDollarIcon from '../icons/non-prefilled/sidebar/currencyDollar.svg';
import flagIcon from '../icons/non-prefilled/sidebar/flag.svg';
import focusIcon from '../icons/non-prefilled/sidebar/focus.svg';
import folderBookmarkIcon from '../icons/non-prefilled/sidebar/folderBookmark.svg';
import graduationHatIcon from '../icons/non-prefilled/sidebar/graduationHat.svg';
import graphStatsIcon from '../icons/non-prefilled/sidebar/graphStats.svg';
import graphStatsCircleIcon from '../icons/non-prefilled/sidebar/graphStatsCircle.svg';
import headphonesIcon from '../icons/non-prefilled/sidebar/headphones.svg';
import labFlaskExperimentIcon from '../icons/non-prefilled/sidebar/labFlaskExperiment.svg';
import languageCodeIcon from '../icons/non-prefilled/sidebar/languageCode.svg';
import lightbulbIcon from '../icons/non-prefilled/sidebar/lightbulb.svg';
import markdownIcon from '../icons/non-prefilled/sidebar/markdown.svg';
import megaphoneIcon from '../icons/non-prefilled/sidebar/megaphone.svg';
import modulePuzzleIcon from '../icons/non-prefilled/sidebar/modulePuzzle.svg';
import quillCircleIcon from '../icons/non-prefilled/sidebar/quillCircle.svg';
import rulerIcon from '../icons/non-prefilled/sidebar/ruler.svg';
import scriptTextIcon from '../icons/non-prefilled/sidebar/scriptText.svg';
import sliderHorizontalIcon from '../icons/non-prefilled/sidebar/sliderHorizontal.svg';
import sparkleIcon from '../icons/non-prefilled/sidebar/sparkle.svg';
import stopwatchIcon from '../icons/non-prefilled/sidebar/stopwatch.svg';
import tagsIcon from '../icons/non-prefilled/sidebar/tags.svg';
import vectorsAnchorIcon from '../icons/non-prefilled/sidebar/vectorsAnchor.svg';
import workflowBranchIcon from '../icons/non-prefilled/sidebar/workflowBranch.svg';
import wrenchIcon from '../icons/non-prefilled/sidebar/wrench.svg';
import { useAllTopicMap } from '../topic/useAllTopics';
import SidebarItems from './SidebarItems';
import SidebarSection from './SidebarSection';
import styles from './SidebarSections.module.scss';
import isNotDetached from './isNotDetached';

const POST_FRAGMENT = gql(`
  fragment SidebarSections_post on Post {
    id
    ...SidebarItems_post
  }
`);

type PostFragment = DocumentType<typeof POST_FRAGMENT>;

const SIDEBAR_SECTIONS_QUERY = gql(`
  query SidebarSections {
    currentSession {
      user {
        id
        sidebarSections {
          id
          name
          color
          icon {
            preset
          }
          items {
            ... on Post {
              id
              ...SidebarSections_post
            }
            ... on Topic {
              id
              detached
            }
          }
        }
      }
    }
  }
`);

const SIDEBAR_ICONS = [
  { name: 'favorite', icon: favoriteIcon },
  { name: 'flag', icon: flagIcon },
  { name: 'mention', icon: mentionIcon },
  { name: 'verified', icon: verifiedIcon },
  { name: 'focus', icon: focusIcon },
  { name: 'modulePuzzle', icon: modulePuzzleIcon },
  { name: 'pin', icon: pinOutlineIcon },
  { name: 'sparkle', icon: sparkleIcon },
  { name: 'text', icon: textIcon },
  { name: 'open', icon: openIcon },
  { name: 'private', icon: privateIcon },
  { name: 'languageCode', icon: languageCodeIcon },
  { name: 'shortcut', icon: shortcutIcon },
  { name: 'android', icon: androidIcon },
  { name: 'graphStatsCircle', icon: graphStatsCircleIcon },
  { name: 'wrench', icon: wrenchIcon },
  { name: 'workflowBranch', icon: workflowBranchIcon },
  { name: 'tags', icon: tagsIcon },
  { name: 'vectorsAnchor', icon: vectorsAnchorIcon },
  { name: 'bookClose', icon: bookCloseIcon },
  { name: 'ruler', icon: rulerIcon },
  { name: 'stopwatch', icon: stopwatchIcon },
  { name: 'angleBrackets', icon: angleBracketsIcon },
  { name: 'headphones', icon: headphonesIcon },
  { name: 'lightbulb', icon: lightbulbIcon },
  { name: 'megaphone', icon: megaphoneIcon },
  { name: 'colorBrush', icon: colorBrushIcon },
  { name: 'markdown', icon: markdownIcon },
  { name: 'folderBookmark', icon: folderBookmarkIcon },
  { name: 'quillCircle', icon: quillCircleIcon },
  { name: 'bookmarks', icon: bookmarksIcon },
  { name: 'graphStats', icon: graphStatsIcon },
  { name: 'cloud', icon: cloudIcon },
  { name: 'aiChipSpark', icon: aiChipSparkIcon },
  { name: 'currencyDollar', icon: currencyDollarIcon },
  { name: 'cog', icon: cogIcon },
  { name: 'sliderHorizontal', icon: sliderHorizontalIcon },
  { name: 'scriptText', icon: scriptTextIcon },
  { name: 'labFlaskExperiment', icon: labFlaskExperimentIcon },
  { name: 'graduationHat', icon: graduationHatIcon },
];
const SIDEBAR_ICONS_MAP = SIDEBAR_ICONS.reduce<Record<string, string>>(
  (acc, { name, icon }) => ({ ...acc, [name]: icon }),
  {},
);

interface SidebarSectionsProps {
  activeTopics: Record<string, boolean>;
  expandedTopics: Record<string, boolean>;
}

const SidebarSections = ({
  activeTopics,
  expandedTopics,
}: SidebarSectionsProps) => {
  const allTopicsMap = useAllTopicMap();

  const { data } = useQuery(SIDEBAR_SECTIONS_QUERY);
  const sections = data?.currentSession?.user.sidebarSections ?? [];

  return (
    <>
      {sections.map((section) => {
        if (['all_topics', 'my_topics', 'integrations'].includes(section.id)) {
          return null;
        }

        const icon = section.icon?.preset
          ? SIDEBAR_ICONS_MAP[section.icon.preset]
          : null;

        const posts = section.items.filter(
          (item): item is PostFragment => item.__typename === 'Post',
        );
        const topics = _.compact(
          section.items.map((item) =>
            item.__typename === 'Topic' ? allTopicsMap[item.id] : null,
          ),
        ).filter(isNotDetached);

        return (
          <SidebarSection
            key={section.id}
            id={section.id}
            title={section.name}
            initiallyCollapsed
            icon={
              <span
                className={styles.sectionIcon}
                style={
                  section.color ? { color: `#${section.color}` } : undefined
                }
              >
                <SVGIcon>{icon ?? favoriteIcon}</SVGIcon>
              </span>
            }
            empty={posts.length === 0 && topics.length === 0}
          >
            <SidebarItems
              activeTopics={activeTopics}
              expandedTopics={expandedTopics}
              posts={posts}
              orderIds={section.items.map((item) => item.id)}
              topics={topics}
            />
          </SidebarSection>
        );
      })}
    </>
  );
};

export default SidebarSections;
